/* eslint react-hooks/exhaustive-deps: "warn" */
import { useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './MsvRegulations.module.scss';

type Translate = (key: string) => string;

type Regulation = {
  tab: string;
  cite: string;
  what: string;
  meta: [string, string][];
};

// Statute names (tab) and legal citations (cite) are proper nouns — left
// unwrapped. Dates in meta values are left as-is; label + prose values wrapped.
const getRegulations = (t: Translate): Regulation[] => [
  {
    tab: 'EU Digital Services Act',
    cite: 'Regulation (EU) 2022/2065, Article 30',
    what: t(
      'Obtain the trade register and registration number before the trader lists. Make best efforts to assess whether that information is reliable, using freely accessible official databases. Suspend the trader where inaccurate information is not corrected.',
    ),
    meta: [
      [t('Applies from'), '17 Feb 2024'],
      [t('Binds'), t('Platforms enabling distance contracts')],
      [t('Field demanded'), t('Trade register + registration number')],
      [t('On failure'), t('Suspension of the trader')],
    ],
  },
  {
    tab: 'INFORM Consumers Act',
    cite: 'INFORM Consumers Act · United States · FTC enforced',
    what: t(
      'Collect bank, tax identification and contact details within ten days of a seller passing 200 sales and $5,000 in gross revenue. Verify within a further ten days. Recertify annually. Disclose seller identity to buyers above $20,000.',
    ),
    meta: [
      [t('In force'), '27 Jun 2023'],
      [t('Trigger'), t('200 sales and $5,000 in 12 months')],
      [t('Clock'), t('10 days collect, 10 days verify')],
      [t('On failure'), t('Suspend future sales activity')],
    ],
  },
  {
    tab: 'DAC7',
    cite: 'Council Directive (EU) 2021/514',
    what: t(
      'Collect legal name, primary address, business registration number, tax identification number and VAT number for entity sellers. Confirm reliability using available records and free electronic interfaces. Report to the tax authority annually.',
    ),
    meta: [
      [t('Reporting from'), t('2023 period')],
      [t('Filing deadline'), '31 January'],
      [t('Field demanded'), t('Business registration number + VAT')],
      [t('On failure'), t('Penalties set per member state')],
    ],
  },
  {
    tab: 'GPSR',
    cite: 'Regulation (EU) 2023/988 · General Product Safety',
    what: t(
      'Every offer must carry the identity and contact detail of the economic operator responsible for the product in the EU. Traceability of that operator, and cooperation on recalls, sits with the marketplace.',
    ),
    meta: [
      [t('Applies from'), '13 Dec 2024'],
      [t('Binds'), t('Providers of online marketplaces')],
      [t('Field demanded'), t('Responsible economic operator in the EU')],
      [t('On failure'), t('Listing removal and enforcement')],
    ],
  },
];

export const MsvRegulations = () => {
  const { t } = useInt();
  const [active, setActive] = useState(0);
  const [fading, setFading] = useState(false);

  const regulations = getRegulations(t);

  const switchTab = (i: number) => {
    if (i === active) return;
    setFading(true);
    setTimeout(() => {
      setActive(i);
      setFading(false);
    }, 200);
  };

  const reg = regulations[active];

  return (
    <Section background="linear-gradient(165deg, #042354 0%, #081d3d 100%)" className={styles.root}>
      <div className={styles.content}>
        <div className={styles.head}>
          <TitleBlock title={t('Four regimes. One control.')} theme="dark" align="left" />
        </div>

        <div className={styles.tabs}>
          {regulations.map((r, i) => (
            <button
              key={r.tab}
              className={cn(styles.tab, { [styles.tabOn]: active === i })}
              onClick={() => switchTab(i)}
            >
              {r.tab}
            </button>
          ))}
        </div>

        <div className={cn(styles.panel, { [styles.panelFade]: fading })}>
          <div className={styles.panelLeft}>
            <span className={styles.cite}>{reg.cite}</span>
            <p className={styles.what}>{reg.what}</p>
          </div>
          <div className={styles.panelRight}>
            <div className={styles.metaTable}>
              {reg.meta.map(([k, v]) => (
                <div key={k} className={styles.metaRow}>
                  <span className={styles.metaKey}>{k}</span>
                  <span className={styles.metaVal}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};
